import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { getAccessStatus } from 'lib/courses';
import { Avatar, Link } from 'components/common';
import { BoxRow } from 'components/common/box';
import { CourseChaptersStatus } from '.';

import type { CourseProgress as CourseProgressData } from 'shared-types';
import type { CourseInView } from 'components/courses/index/Courses';

interface Props {
  chapters: CourseInView['chapters'];
  course: CourseInView;
  languageVersion: string;
  progressData: CourseProgressData;
}

const CourseChapters = ({
  chapters,
  course,
  languageVersion,
  progressData
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme, user } = useReduxState();

  const { canBeOpened } = getAccessStatus({ course, user });

  return (
    <div className="chapters">
      {chapters.map((chapter) => (
        <BoxRow key={chapter.id}>
          <div className="chapter">
            <div className="chapter-number">
              <Avatar fontSize="1.4rem">{chapter.number}</Avatar>
            </div>
            <div className="chapter-title-wrapper">
              {canBeOpened ? (
                <Link
                  as={`/courses/${course.slug}/${chapter.slug}`}
                  href="/courses/[...courseParams]"
                  linkColor={theme.textColor}
                  style={{
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    fontWeight: 600
                  }}
                  text={
                    chapter.titleLocales?.[languageVersion] || chapter.title
                  }
                />
              ) : (
                <div className="chapter-title">
                  {chapter.titleLocales?.[languageVersion] || chapter.title}
                </div>
              )}

              {!chapter.publishDate ? (
                <div className="unpublished">
                  {`${messages.commonNotPublished}`}
                </div>
              ) : Date.parse(chapter.publishDate as string) > dateNow ? (
                <div className="unpublished">
                  {`${messages.commonWillPublish} ${getFormattedDate({
                    date: chapter.publishDate,
                    dateFormat: 'dd.MM.y HH:mm'
                  })}`}
                </div>
              ) : null}
            </div>
            <div className="chapter-status">
              <CourseChaptersStatus
                chapterId={chapter.id}
                completionEnabled={course.completionEnabled}
                progressData={progressData?.chapters?.[chapter.id]}
                requiredReadingProgress={course.completionReading}
                requiredTasksProgress={course.completionTasks}
                requiredVideoProgress={course.completionVideos}
              />
            </div>
          </div>
        </BoxRow>
      ))}

      <style jsx>{`
        .chapters {
          flex-grow: 1;
        }
        .chapter {
          align-items: center;
          display: flex;
          gap: 15px;
          padding: 5px 0;
        }
        .chapter-title-wrapper {
          flex-grow: 1;
        }
        .chapter-title {
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 600;
          text-decoration: none;
        }
        .unpublished {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default CourseChapters;
